"use client"

import * as React from "react"
import { DayPicker, type DropdownProps } from "react-day-picker"
import { fr, enUS } from "date-fns/locale"
import { format } from "date-fns"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

const LOCALE_MAP: Record<string, typeof fr> = { fr, en: enUS }
import { useIsMobile } from "@/hooks/use-mobile"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
    /** Enable dropdown navigation for month/year (default: true) */
    showDropdowns?: boolean
    /** Start year for year dropdown (default: 1920) */
    startYear?: number
    /** End year for year dropdown (default: 2050) */
    endYear?: number
}

/**
 * Capitalize first letter of a string.
 */
function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Hybrid Dropdown Component:
 * - Desktop (≥768px): Custom Radix Select with Argalis styling
 * - Mobile (<768px): Native select with visible label (iOS wheel / Android bottom sheet)
 * - SSR/Hydration: Native select as safe fallback
 * 
 * Note: react-day-picker v9 uses a pattern where the select is hidden (opacity:0)
 * and overlays a visible label. We replicate this for mobile.
 */
function HybridDropdown({
    value,
    onChange,
    options,
    "aria-label": ariaLabel,
    className,
}: DropdownProps) {
    const isMobile = useIsMobile()
    const selectedOption = options?.find((opt) => opt.value === value)

    // On mobile OR during SSR (undefined): use native select with visible label
    // The select is positioned absolutely over the label and is transparent
    if (isMobile === undefined || isMobile) {
        return (
            <div className="rdp-dropdown_root relative inline-flex items-center">
                {/* Visible label showing selected value */}
                <span className="rdp-caption_label text-sm font-medium px-2 py-1 flex items-center gap-1">
                    {selectedOption?.label}
                    <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                </span>
                {/* Hidden select overlay - captures clicks and triggers native picker */}
                <select
                    value={value}
                    onChange={(e) => onChange?.(e)}
                    aria-label={ariaLabel}
                    className={cn(
                        "rdp-dropdown absolute inset-0 w-full opacity-0 cursor-pointer",
                        className
                    )}
                >
                    {options?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        )
    }

    // On desktop: use custom Radix Select with Argalis styling
    return (
        <Select
            value={String(value)}
            onValueChange={(newValue) => {
                // Simulate a change event for react-day-picker
                const syntheticEvent = {
                    target: { value: newValue },
                } as React.ChangeEvent<HTMLSelectElement>
                onChange?.(syntheticEvent)
            }}
        >
            <SelectTrigger
                className={cn(
                    "h-9 gap-1 border-border text-sm font-medium focus:ring-primary",
                    // Adjust width based on content type (keep compact to not overlap nav arrows)
                    ariaLabel?.includes("year") ? "w-[78px]" : "w-[110px]",
                    className
                )}
                aria-label={ariaLabel}
            >
                <SelectValue>{selectedOption?.label}</SelectValue>
            </SelectTrigger>
            <SelectContent
                className="max-h-[240px] overflow-hidden"
                position="popper"
                sideOffset={4}
            >
                {options?.map((option) => (
                    <SelectItem
                        key={option.value}
                        value={String(option.value)}
                        className="focus:bg-accent focus:text-primary cursor-pointer"
                    >
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

/**
 * Calendar component using react-day-picker v9.
 * 
 * Features:
 * - French locale by default
 * - Hybrid dropdown navigation (Radix on desktop, native on mobile)
 * - Configurable year range (1920-2050 by default)
 * - Months are capitalized (Janvier, Février, etc.)
 */
function Calendar({
    className,
    showOutsideDays = true,
    showDropdowns = true,
    startYear = 1920,
    endYear = 2050,
    ...props
}: CalendarProps) {
    const { i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language?.slice(0, 2)] || fr

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn("p-3", className)}
            locale={locale}
            captionLayout={showDropdowns ? "dropdown" : "label"}
            startMonth={new Date(startYear, 0)}
            endMonth={new Date(endYear, 11)}
            formatters={{
                // Capitalize month name in dropdown
                formatMonthDropdown: (month) =>
                    capitalize(format(month, "MMMM", { locale })),
                // Capitalize month in caption when not using dropdown
                formatCaption: (month) =>
                    capitalize(format(month, "MMMM yyyy", { locale })),
            }}
            components={{
                Chevron: ({ orientation }) => (
                    orientation === "left"
                        ? <ChevronLeft className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />
                ),
                // Use hybrid dropdown: Radix on desktop, native on mobile
                Dropdown: HybridDropdown,
            }}
            {...props}
        />
    )
}
Calendar.displayName = "Calendar"

export { Calendar }
